import { Link } from 'react-router-dom';
import Seo, { CONTACT_EMAIL, SITE_NAME, SITE_URL } from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const values = [
  {
    icon: 'ri-emotion-happy-line',
    title: '内容原则：先还原现场',
    text: '每一段名场面都会交代前因后果，而不是只截取最好笑的一句。我们更希望你看完之后能真的讲给别人听。',
    accent: 'primary',
  },
  {
    icon: 'ri-shield-check-line',
    title: '隐私原则：一律匿名化',
    text: '站内出现的网友昵称、头像与群聊记录都经过化名处理。我们不会公开任何可定位到真实个人的信息。',
    accent: 'secondary',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: '整理原则：标注来源类型',
    text: '每条内容都会注明属于热评、弹幕、名场面还是群聊片段，方便你按兴趣选择，也避免把不同语境混在一起。',
    accent: 'accent',
  },
  {
    icon: 'ri-refresh-line',
    title: '更新原则：热榜每日重排',
    text: '榜单不是固定的，会根据站内互动情况每天重新排序。昨天的高能今天可能已经退居二线，这正是玩梗文化的常态。',
    accent: 'primary',
  },
];

const accentMap: Record<string, { icon: string; border: string }> = {
  primary: { icon: 'bg-primary-100 text-primary-600', border: 'border-background-200' },
  accent: { icon: 'bg-accent-100 text-accent-700', border: 'border-background-200' },
  secondary: { icon: 'bg-secondary-100 text-secondary-700', border: 'border-background-200' },
};

const testimonials = [
  {
    name: '每天刷三遍',
    role: '常年潜水的老网友',
    text: '以前找梗要翻好几个平台的评论区，现在直接在这里按分类看，省事太多了。热评精选那块我会反复看。',
  },
  {
    name: '接梗小能手',
    role: '群聊气氛组',
    text: '群里的梗基本都是从这儿搬的。尤其是「接梗合集」，看多了自己接梗都顺了不少。',
  },
  {
    name: '排版强迫症',
    role: '内容爱好者',
    text: '最喜欢的是每段名场面都会讲清楚前因后果，不是只截一句就完事，看完真的能复述给别人听。',
  },
];

export default function About() {
  const aboutJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: `关于我们 | ${SITE_NAME}`,
      url: `${SITE_URL}/about`,
      inLanguage: 'zh-CN',
      description: '了解糖心吃瓜的品牌定位、内容原则与整理理念。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      email: CONTACT_EMAIL,
      description: '聚合互联网热评、弹幕、名场面与群聊高能片段的玩梗文化门户。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_URL}/about` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`关于我们 | ${SITE_NAME} - 玩梗文化的内容整理者`}
        description="了解糖心吃瓜的品牌定位、内容原则与整理理念。我们专注把互联网上的热评、弹幕、名场面与群聊高能片段整理得清楚好读。"
        keywords="糖心吃瓜介绍,关于我们,玩梗文化,内容原则,名场面整理"
        path="/about"
        jsonLd={aboutJsonLd}
        ogType="website"
      />

      <Navbar />

      <main className="pb-16 pt-24 md:pt-28">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <header className="mt-6 grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <div>
              <h1 className="text-3xl text-foreground-950 md:text-5xl">
                我们只做一件事：把玩梗现场整理清楚
              </h1>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
                糖心吃瓜是一个专注于互联网玩梗文化的内容站点。我们不生产段子，而是把散落在评论区、
                弹幕区、群聊记录里的那些高能瞬间，用统一的结构重新整理，让你不用在多个平台之间来回切换，
                就能一次看完最热闹的那几段。
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/topics"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-compass-3-line" aria-hidden="true" />
                  去话题分类逛逛
                </Link>
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-send-plane-line" aria-hidden="true" />
                  投稿与合作
                </Link>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20warm%20illustration%20of%20overlapping%20chat%20bubbles%20and%20magnifying%20glass%20shapes%20arranged%20neatly%2C%20coral%20orange%20amber%20gold%20and%20jade%20green%20palette%20on%20a%20cream%20background%2C%20bold%20flat%20graphic%20design%2C%20calm%20organized%20yet%20playful%20mood%2C%20no%20people%2C%20no%20text&width=1000&height=760&seq=tangxin-about-01&orientation=portrait"
                alt="暖色调插画中整齐排列的气泡与放大镜图形，象征对内容的有序整理"
                title="糖心吃瓜 内容整理理念"
                className="h-64 w-full object-top md:h-[420px]"
              />
            </div>
          </header>

          <section className="mt-14 grid gap-6 md:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="text-lg text-foreground-950 md:text-xl">网站定位</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                做玩梗文化里「最好翻」的那一本合集。不追热点速度，而是追内容的完整度和可读性，
                让每一段内容都能独立看懂。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="text-lg text-foreground-950 md:text-xl">面向谁</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                面向喜欢看热评、刷弹幕、在群里接梗的网友。你不需要是资深玩家，只要想看热闹，
                就能在这里找到舒服的入口。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="text-lg text-foreground-950 md:text-xl">发展理念</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                网络梗会过时，但围观热闹这件事不会。我们希望建立一个既能跟上节奏，
                又能沉淀内容的长期空间。
              </p>
            </div>
          </section>

          <section className="mt-14">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-sm font-medium text-primary-600">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                内容原则
              </div>
              <h2 className="mt-3 text-2xl text-foreground-950 md:text-4xl">我们怎么对待每一条内容</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                这四条原则贯穿在每一次整理里，也是我们和单纯搬运之间最大的差别。
              </p>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {values.map((value) => {
                const style = accentMap[value.accent] ?? accentMap.primary;
                return (
                  <div
                    key={value.title}
                    className={`rounded-lg border bg-background-50 p-5 md:p-6 ${style.border}`}
                  >
                    <span
                      className={`flex h-11 w-11 items-center justify-center rounded-lg text-xl ${style.icon}`}
                    >
                      <i className={value.icon} aria-hidden="true" />
                    </span>
                    <h3 className="mt-4 text-base text-foreground-950 md:text-lg">{value.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.text}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="mt-14 rounded-lg bg-secondary-950 p-6 text-background-100 md:p-10">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-sm font-medium text-accent-300">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
                用户口碑
              </div>
              <h2 className="mt-3 text-2xl text-background-50 md:text-4xl">他们在站内留下的评价</h2>
            </div>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {testimonials.map((item) => (
                <blockquote
                  key={item.name}
                  className="flex h-full flex-col rounded-lg border border-secondary-800 bg-secondary-900 p-5"
                >
                  <i className="ri-double-quotes-l text-2xl text-accent-400" aria-hidden="true" />
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-background-200">
                    {item.text}
                  </p>
                  <footer className="mt-5 border-t border-secondary-800 pt-4">
                    <p className="text-sm text-background-50">{item.name}</p>
                    <p className="mt-0.5 text-xs text-background-400">{item.role}</p>
                  </footer>
                </blockquote>
              ))}
            </div>
          </section>

          <section className="mt-14 grid gap-6 rounded-lg border border-background-200 bg-background-100 p-6 md:grid-cols-2 md:p-8">
            <div>
              <h2 className="text-xl text-foreground-950 md:text-2xl">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                想投稿玩梗现场、反馈内容问题，或者聊聊合作，都可以直接发邮件给我们。
                我们通常会在两到三个工作日内回复。
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                rel="nofollow"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-mail-line" aria-hidden="true" />
                {CONTACT_EMAIL}
              </a>
            </div>
            <div className="rounded-lg bg-background-50 p-5">
              <h3 className="text-base text-foreground-950">投稿时可以附上</h3>
              <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-0.5 text-secondary-600" aria-hidden="true" />
                  这段玩梗现场大概发生了什么
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-0.5 text-secondary-600" aria-hidden="true" />
                  属于热评、弹幕、名场面还是群聊片段
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-0.5 text-secondary-600" aria-hidden="true" />
                  方便联系你的邮箱，便于核实与致谢
                </li>
              </ul>
              <Link
                to="/contact"
                className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                前往投稿表单
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}